/**
 * Shop Layout Wrapper
 * Wraps all customer shop pages with:
 * - Store availability check (subscription, suspension, etc.)
 * - Translation context provider
 * - Common loading state
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LanguageProvider } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import StoreUnavailable from '../../pages/vendor-shop/StoreUnavailable';

interface ShopLayoutProps {
  children: React.ReactNode;
}

interface StoreStatus {
  is_available: boolean;
  status: string;
  message: string;
}

const ShopLayout: React.FC<ShopLayoutProps> = ({ children }) => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const { token, isLoading: isAuthLoading } = useAuth();
  const [storeStatus, setStoreStatus] = useState<StoreStatus | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [userLanguage, setUserLanguage] = useState('en');
  const [isBanned, setIsBanned] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Check store availability on mount
  useEffect(() => {
    const checkStoreStatus = async () => {
      if (!botUsername) {
        setIsChecking(false);
        return;
      }

      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/status`);
        const data = await res.json();

        if (data.success && data.data) {
          setStoreStatus(data.data);
        } else {
          // Default to available on error (fail open for better UX)
          setStoreStatus({ is_available: true, status: 'unknown', message: '' });
        }
      } catch (error) {
        console.error('Store status check failed:', error);
        // Default to available on error
        setStoreStatus({ is_available: true, status: 'unknown', message: '' });
      } finally {
        setIsChecking(false);
      }
    };

    checkStoreStatus();
  }, [baseUrl, botUsername]);

  // Check if user is banned - runs after authentication completes
  useEffect(() => {
    const checkBanStatus = async () => {
      // Wait for auth to complete and have a token
      if (isAuthLoading || !token || !botUsername) {
        return;
      }

      try {
        const res = await fetch(`${baseUrl}/api/shop/${botUsername}/customer/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();

        // Check if user is banned
        if (data.code === 'CUSTOMER_BANNED' || data.is_banned) {
          setIsBanned(true);
        }
      } catch (e) {
        // Fail open - don't block on error
      }
    };

    checkBanStatus();
  }, [baseUrl, botUsername, token, isAuthLoading]);

  // Get user's language preference - DATABASE is the source of truth
  useEffect(() => {
    const fetchUserLanguage = async () => {
      // If authenticated, ALWAYS fetch from database first (source of truth)
      // This ensures language changes in native bot are synced to MiniApp
      if (token && botUsername) {
        try {
          const res = await fetch(`${baseUrl}/api/shop/${botUsername}/customer/profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success && data.data?.language) {
            const dbLanguage = data.data.language;
            // Update localStorage to match database
            localStorage.setItem('miniapp_language', dbLanguage);
            setUserLanguage(dbLanguage);
            console.log('Language synced from database:', dbLanguage);
            return;
          }
        } catch (e) {
          console.warn('Failed to fetch language from profile:', e);
          // Fall through to fallbacks
        }
      }

      // Fallback 1: localStorage (for non-authenticated state)
      const storedLang = localStorage.getItem('miniapp_language');
      if (storedLang) {
        setUserLanguage(storedLang);
        return;
      }

      // Fallback 2: Telegram user language
      const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
      if (tgLang) {
        setUserLanguage(tgLang);
        return;
      }

      // Default to 'en'
      setUserLanguage('en');
    };

    fetchUserLanguage();
  }, [baseUrl, botUsername, token]);

  // Show loading while checking
  if (isChecking) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  // Show unavailable page if store is blocked
  if (storeStatus && !storeStatus.is_available) {
    return <StoreUnavailable />;
  }

  // Show banned page if user is banned
  if (isBanned) {
    return <AccountSuspended />;
  }

  // Wrap children with language provider
  return (
    <LanguageProvider botUsername={botUsername || ''} defaultLanguage={userLanguage}>
      {children}
    </LanguageProvider>
  );
};

// Account Suspended Component
const AccountSuspended: React.FC = () => {
  return (
    <SuspendedContainer>
      <SuspendedIcon>🚫</SuspendedIcon>
      <SuspendedTitle>Account Suspended</SuspendedTitle>
      <SuspendedMessage>
        Your account has been suspended. Please contact support for assistance.
      </SuspendedMessage>
    </SuspendedContainer>
  );
};

const SuspendedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: var(--tg-theme-bg-color, #1a1a1a);
  text-align: center;
`;

const SuspendedIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const SuspendedTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #fff);
  margin: 0 0 12px 0;
`;

const SuspendedMessage = styled.p`
  font-size: 16px;
  color: var(--tg-theme-hint-color, #999);
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--tg-theme-bg-color, #1a1a1a);
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--tg-theme-secondary-bg-color, #2c2c2e);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default ShopLayout;
